import styled from "styled-components"
import { theme } from "../../../../styles/theme"
import { useGetDrivingDetail } from "../../../../api/history"
import { GetDrivingDetailParams } from "../../../../types"
import { MouseEventHandler, useMemo, useRef } from "react"
import { Etc } from "../../../Etc"
import { Svg_Info } from "../../../Svg"

interface Props {
  apiParams: GetDrivingDetailParams
}

export const Section_History_DrivingDetails = ({apiParams}: Props) => {
  const
  Unit = useRef({
    count: "회",
    kmh: "km/h",
    seconds: "s",
    km: "km"
  } as const).current,
  ref = useRef({
    /**
     * BSD
     * 제외 []
     * TS
     * 제외 [totalLineChange, totalLineBreak]
     */
    fields: [
      {field: "startDateTm", label: "주행시작시간"},
      {field: "endDateTm", label: "주행종료시간"},
      {field: "distance", label: "총이동거리", unit: Unit.km},
      {field: "evetNum", label: "충격횟수", unit: Unit.count},
      {field: "motNum", label: "모션횟수", unit: Unit.count},
      {field: "overSpeedDis", label: "과속거리", unit: Unit.km},
      {field: "overSpeedDriveingTime", label: "과속시간", unit: Unit.seconds},
      {field: "speedAvg", label: "평균속도", unit: Unit.kmh},
      {field: "speedMax", label: "최고속도", unit: Unit.kmh},
      {field: "totalFrontWarning", label: "앞차출발", unit: Unit.count},
      // {field: "totalLineChange", label: "차선변경", unit: Unit.count},
      // {field: "totalLineBreak", label: "차선이탈", unit: Unit.count},
      {field: "sigLeft", label: "좌측방향지시등", unit: Unit.count},
      {field: "sigRight", label: "우측방향지시등", unit: Unit.count},
      {field: "sigGear", label: "후진등", unit: Unit.count},
      {field: "sigBreak", label: "브레이크등", unit: Unit.count},
      {field: "leftAndRight", label: "좌우방향지시등", unit: Unit.count},
      {field: "totalFastStart", label: "급출발", unit: Unit.count, desc: "속도가 0인 상태에서 출발하여\n3초동안 초당 속도가 11km/h 이상 올라간 경우 (처음 속도 0)"},
      {field: "totalFastStop", label: "급정거", unit: Unit.count, desc: "속도가 3초동안 초당 7.5km/h 이상 감속하여\n차량이 멈춘 경우 (마지막 속도 0)"},
      {field: "totalFastAccel", label: "급가속", unit: Unit.count, desc: "속도가 3초동안 초당 11km/h 이상 증가된 경우"},
      {field: "totalFastBreak", label: "급감속", unit: Unit.count, desc: "속도가 3초동안 초당 11km/h 이상 감속된 경우"},
      {field: "totalPeople", label: "사람감지", unit: Unit.count},
      {field: "totalCar", label: "자동차감지", unit: Unit.count},
      {field: "totalAuto", label: "그외감지", unit: Unit.count},
    ]
  }).current,
  {data, isLoading, isError} = useGetDrivingDetail(apiParams),
  list = useMemo(() => {
    const result: {
      label: string,
      value: string | number,
      unit?: string,
      desc?: string,
    }[] = []

    if (data) {
      for (const item of ref.fields)
        if (item.field in data)
          result.push({...item, value: data[item.field]})
    }

    return result
  }, [data])

  const onHover: MouseEventHandler = e => {
    try {
      const
      {type, target: desc} = e
      if (desc instanceof HTMLDivElement && desc.classList.contains("desc")) {
        
        if (type === "mouseover") {
          const
          HEADER_HEIGHT = 65,
          SPACE = 5,
          ICON_HALF_SIZE = desc.offsetWidth / 2,
          tooltip = desc.querySelector(":scope > .tooltip") as HTMLDivElement,
          rect = desc.getBoundingClientRect(),
          tooltipSize = {
            x: tooltip.offsetWidth,
            y: tooltip.offsetHeight,
          },
          origin = {
            x: rect.left + ICON_HALF_SIZE,
            y: rect.top + ICON_HALF_SIZE,
          },
          xOffset = window.innerWidth - (origin.x + tooltipSize.x / 2),
          isRightOverlap = xOffset < 0,
          isUp = ((HEADER_HEIGHT + tooltipSize.y + SPACE + ICON_HALF_SIZE) < origin.y) ? -1 : 1,
          trans = {
            x: isRightOverlap ? xOffset : 0,
            y: (tooltipSize.y / 2 + ICON_HALF_SIZE + SPACE) * isUp,
          }
          desc.classList.add("hover")
          tooltip.style.left = `${(origin.x - tooltipSize.x / 2).toFixed(0)}px`
          tooltip.style.top = `${(origin.y - tooltipSize.y / 2).toFixed(0)}px`
          tooltip.style.transform = `translate(${trans.x}px, ${trans.y}px)`

        } else {
          desc.classList.remove("hover")
        }
      }

    } catch (e) {
      console.error(`onHover > `, e)
    }
  }

  return (
    <SSection className="DailyDetails">
      <Etc.CurrentState isLoading={isLoading} isError={isError} />
      <ul className="SCROLL_BG_TRANS" onMouseOver={onHover} onMouseOut={onHover}>
        {list?.map(({label, value, unit, desc}) => {
          return (
            <li key={label}>
              <div className="label">
                <label>{label}</label>
                {!!desc && (
                  <div className="desc">
                    <Svg_Info />
                    <div className="tooltip">{desc}</div>
                  </div>
                )}
              </div>
              <div className="value">
                <strong>{unit == Unit.count ? Number(value).toLocaleString() : value}</strong>
                {unit && <span>{unit}</span>}
              </div>
            </li>
          )
        })}
      </ul>
    </SSection>
  )
}

const SSection = styled.section`
  position: relative;

  & > ul {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    overflow-x: hidden;
    overflow-y: auto;
  
    & > li {
      padding: 10px;
      width: 150px;
      white-space: nowrap;
  
      & > .label {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: center;
        gap: 5px;

        & > label {
          color: ${theme.colors.white};
        }
        & > .desc {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 22px;
          height: 22px;
          color: #b4b4b4;
          cursor: pointer;

          & > svg {
            pointer-events: none;
          }
          & > .tooltip {
            position: fixed;
            z-index: 3;
            visibility: hidden;
            pointer-events: none;
            padding: 5px;
            border-radius: 8px;
            color: ${theme.colors.white};
            font-size: 14px;
            background-color: white;
            color: ${theme.colors.black};
            white-space: pre;
          }
          &.hover {
            & > .tooltip {
              visibility: visible;
            }
          }
        }
      }
      & > .value {
        display: flex;
        align-items: center;
        gap: 4px;

        & > strong {
          font-size: 18px;
          color: ${theme.colors.yellowgreen};
        }
        & > span {
          font-size: 14px;
          color: #b4b4b4;
        }
      }
    }
  }
`